import { sanitiseSuppliedNames } from '../../../../services/EpisodeScriptService';
import type { BasilHttp } from '../write/writeTypes';

/**
 * The book's own words for the three things the script prints as ids (Basil v0.1.1).
 *
 * Before this, the script showed `== intro @ L5783328` and `C4894851 (E12)`: the decompiler was
 * supplied the memory bank's variables and nothing else, so every character, mood and location
 * printed as the synthetic id spelling. The three rows the engine already serves fill that gap:
 *
 *  * `GET /v1/books/:bookId/characters` → `characters[]`, each `{ id, name }` (a character skin);
 *  * `GET /v1/books/:bookId/locations`  → `locations[]`,  each `{ id, title }`;
 *  * `GET /v1/characters/expressions`   → `expressions[]`, each `{ id, title }` — the global mood
 *    list, not the book's, which is why it takes no book id.
 *
 * FAIL-OPEN is the whole contract. Each read is caught on its own: a request that throws, a
 * maintenance 503 (`components/api.js:60-72` resolves one with an empty body), a body of the wrong
 * shape and a book with no rows all yield NO words for that domain, and a domain with no words is
 * simply not supplied — so the script keeps that domain's ids exactly as it did before. Nothing
 * here shows an error, and nothing here touches the write path.
 *
 * The words are then filtered by the service's own rule (`sanitiseSuppliedNames`): a word reaches
 * the script only when a script written with it parses back to the id it was given for. A writer's
 * word is free text and the script is a grammar; the ones that cannot be spelled keep their id.
 */
/** The two ids a location row carries about its own media. Either may be absent. */
export interface LocationMedia {
  backgroundId: string | null;
  audioId: string | null;
}

export interface BookNames {
  characters: Record<string, string>;
  expressions: Record<string, string>;
  locations: Record<string, string>;
  /**
   * Basil v0.2, P3: what each location has attached, keyed by location id.
   *
   * It rides on the SAME locations read the words come from — the row already carries
   * `backgroundId` and `audioId`, so asking twice would be asking the engine for something it has
   * already said. The two ids are all this is: no title, no url, no asset of any kind.
   */
  locationMedia: Record<string, LocationMedia>;
  /**
   * Whether the locations list actually answered.
   *
   * P3 turns on this distinction and nothing else does: a lookup that FAILED is never shown as an
   * absence, so a scene whose location could not be read says "details unavailable" rather than
   * quietly saying nothing at all.
   */
  locationsRead: boolean;
}

/** No words at all: what a failed, empty or skipped read supplies. */
export const EMPTY_BOOK_NAMES: BookNames = Object.freeze({
  characters: {},
  expressions: {},
  locations: {},
  locationMedia: {},
  locationsRead: false,
}) as BookNames;

/**
 * The engine's name for the player skin's row. The player's name is chosen in the app, so the book
 * stores this placeholder; the script says `Player`, which is the word a writer reads it as. A book
 * that also has a character actually called "Player" keeps that word for whichever id is lower —
 * the duplicate rule decides, and the other keeps its id spelling.
 */
export const PLAYER_SKIN_NAME = '<PlayerName>';

/** What the script calls the player skin. */
export const PLAYER_WORD = 'Player';

export function bookCharactersPath(bookId: number | string): string {
  return `/v1/books/${bookId}/characters`;
}

export function bookLocationsPath(bookId: number | string): string {
  return `/v1/books/${bookId}/locations`;
}

export function expressionsPath(): string {
  return '/v1/characters/expressions';
}

/** One row of any of the three lists: an id and the word beside it, and a location's media ids. */
type NameRow = {
  id?: unknown;
  name?: unknown;
  title?: unknown;
  backgroundId?: unknown;
  audioId?: unknown;
};

/**
 * One list, read fail-open. Anything other than a 200 carrying an array under `key` is no rows.
 *
 * `ok` is the one fact v0.2 added: P3 must tell a list that answered with nothing from a list that
 * never answered, because the first is an absence and the second is not.
 */
async function readList(http: BasilHttp, path: string, key: string): Promise<{ ok: boolean; rows: NameRow[] }> {
  try {
    const response = await http.get<Record<string, unknown>>(path);

    if (response && response.status === 503) {
      return { ok: false, rows: [] };
    }
    const rows = response && response.data ? response.data[key] : null;

    return Array.isArray(rows) ? { ok: true, rows: rows as NameRow[] } : { ok: false, rows: [] };
  } catch {
    return { ok: false, rows: [] };
  }
}

/** The `backgroundId` and `audioId` each location row carries, keyed by the location's id. */
function mediaOf(rows: NameRow[]): Record<string, LocationMedia> {
  const media: Record<string, LocationMedia> = {};

  rows.forEach((row) => {
    if (!row || row.id == null) {
      return;
    }
    media[String(row.id)] = {
      backgroundId: row.backgroundId == null ? null : String(row.backgroundId),
      audioId: row.audioId == null ? null : String(row.audioId),
    };
  });

  return media;
}

/** `id → word` from a list of rows, taking the word from `name` or `title`; rowless ids are skipped. */
function wordsOf(rows: NameRow[], field: 'name' | 'title'): Record<string, string> {
  const words: Record<string, string> = {};

  rows.forEach((row) => {
    if (!row || row.id == null) {
      return;
    }
    const raw = row[field];

    if (typeof raw !== 'string' || raw === '') {
      return;
    }
    words[String(row.id)] = raw === PLAYER_SKIN_NAME ? PLAYER_WORD : raw;
  });

  return words;
}

/**
 * Read the three lists once and return the words the script may print.
 *
 * It never rejects and never throws: the worst outcome is `EMPTY_BOOK_NAMES`, which is the script
 * exactly as it read before this existed.
 *
 * @param http the page's axios instance (or a mock: no test here issues a real request)
 * @param bookId `story.book.id` from the page. A book id that is not a positive number reads nothing.
 */
export async function readBookNames(http: BasilHttp, bookId: number): Promise<BookNames> {
  const book = Number(bookId);

  if (!Number.isFinite(book) || book <= 0) {
    return EMPTY_BOOK_NAMES;
  }
  const [characters, locations, expressions] = await Promise.all([
    readList(http, bookCharactersPath(book), 'characters'),
    readList(http, bookLocationsPath(book), 'locations'),
    readList(http, expressionsPath(), 'expressions'),
  ]);
  const spellable = sanitiseSuppliedNames({
    characters: wordsOf(characters.rows, 'name'),
    expressions: wordsOf(expressions.rows, 'title'),
    locations: wordsOf(locations.rows, 'title'),
  });

  return {
    characters: (spellable.characters ?? {}) as Record<string, string>,
    expressions: (spellable.expressions ?? {}) as Record<string, string>,
    locations: (spellable.locations ?? {}) as Record<string, string>,
    // P3: the media ids come from the ROWS, not from the spellable words. A location whose title
    // the script cannot spell keeps its id on the page and still has its picture and its music.
    locationMedia: mediaOf(locations.rows),
    locationsRead: locations.ok,
  };
}
